import { Avatar, Box, Stack, Typography } from '@mui/material';
import { ModalProps } from '../../../types/ModalTypes';
import { useEffect, useMemo } from 'react';
import { useListGroups } from '../../../hooks/useListGroups';
import { UserRole, UserWithRoles, UserOptions } from '../../../types/User';
import PermissionsMenu from '@brainpool/tunedd-lib/components/PermissionsMenu/PermissionsMenu';

import {
  UpdateGroupMembersInput,
  useUpdateGroupMembers,
} from '../../../hooks/useUpdateGroupMembers';
import { reactiveVars, UserDetailsProps } from '@brainpool/apollo-connect';
import { useReactiveVar } from '@apollo/client/react';
import Button from '@brainpool/components/atoms/Button/Button';
import { Modal } from '@brainpool/components/molecules/Modal/Modal';

export const rolePriority: UserRole[] = ['Owner', 'Editor', 'Reader'];

export function PermissionsModal({ data, handleClose, open = false, type }: ModalProps) {
  const currentUser = useReactiveVar<UserDetailsProps>(reactiveVars.userDetails);

  const {
    listGroups: listCaseGroups,
    data: caseGroupsData,
    loading: caseGroupsLoading,
  } = useListGroups({ useCache: false });

  const {
    updateGroupMembers,
    data: updateGroupMembersData,
    loading: updateGroupLoading,
  } = useUpdateGroupMembers();

  useEffect(() => {
    if (data && data.length > 0) {
      listCaseGroups([{ case_id: data[0].id }], 100);
    }
  }, []);

  useEffect(() => {
    reactiveVars.isLoading(updateGroupLoading);
  }, [updateGroupLoading]);

  const permissionGroups = caseGroupsData?.listGroups[0].items || [];

  const caseUserGroup: UserWithRoles[] = useMemo(() => {
    // Map to track each user's highest priority role
    const userMap = new Map<string, UserWithRoles>();

    // Process all groups and members
    caseGroupsData?.listGroups[0]?.items?.forEach(({ roles, members }) => {
      const role = roles[0]?.role as UserRole;

      members.forEach(({ id, identifier }) => {
        // Skip current user - check both ID and email/identifier
        if (
          (currentUser.userId && id === currentUser.userId) ||
          (currentUser.email && identifier === currentUser.email)
        ) {
          return;
        }

        const existing = userMap.get(id);
        // If user doesn't exist or this role has higher priority, update
        if (
          !existing ||
          (role && rolePriority.indexOf(role) < rolePriority.indexOf(existing.role as UserRole))
        ) {
          userMap.set(id, { id, role, identifier });
        }
      });
    });
    // Convert map to array and sort by role priority
    return Array.from(userMap.values()).sort((a, b) => {
      const roleA = a.role ? rolePriority.indexOf(a.role as UserRole) : Infinity;
      const roleB = b.role ? rolePriority.indexOf(b.role as UserRole) : Infinity;
      return roleA - roleB;
    });
  }, [caseGroupsData, currentUser.userId]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('');
  };

  const handleRemoveMember = (member: UserWithRoles) => {
    const currentGroupIds = permissionGroups
      .filter(({ name }) => name.includes(member.role))
      .map(({ id }) => id);
    const updateGroups: UpdateGroupMembersInput[] = currentGroupIds.map(id => {
      return {
        id: id,
        members: [member.id],
        action: 'REMOVE',
      };
    });

    if (data && data[0] && typeof data[0].id === 'string') {
      updateGroupMembers({
        groups: updateGroups,
        caseIds: [{ case_id: data[0].id }],
      });
    }
  };

  const handleUpdatePermissions = (member: UserWithRoles, option: UserOptions) => {
    const updateGroups: UpdateGroupMembersInput[] = [];
    const newRole = option.value as UserRole;
    const currentRolePriority = rolePriority.indexOf(member.role as UserRole);
    const newRolePriority = rolePriority.indexOf(option.value as UserRole);
    const newGroupId = permissionGroups.find(({ name }) => name.includes(newRole))?.id;

    // if user is not in the group then add them
    if (newRolePriority !== currentRolePriority) {
      updateGroups.push({
        id: newGroupId as string,
        members: [member.id],
        action: 'ADD',
      });
    }
    // New role has lower priority (higher index), remove from higher priority groups
    if (newRolePriority < currentRolePriority) {
      const higherPriorityGroups = permissionGroups.filter(group => {
        const groupRole = rolePriority.find(role => group.name?.includes(role)) as UserRole;
        return groupRole && rolePriority.indexOf(groupRole) < newRolePriority;
      });

      updateGroups.push(
        ...higherPriorityGroups.map(group => ({
          id: group.id,
          members: [member.id],
          action: 'REMOVE' as const,
        }))
      );
    }
    if (updateGroups.length > 0 && data && data[0] && typeof data[0].id === 'string') {
      updateGroupMembers({
        groups: updateGroups,
        caseIds: [{ case_id: data[0].id }],
      });
    }
  };
  const handleOpenModal = (type: ModalProps['type']) => {
    reactiveVars.modalData({
      data,
      type,
      open: true,
    });
  };
  return (
    <Modal
      titleType="h3"
      title="Permissions"
      open={open}
      onClose={
        handleClose ??
        (() => {
          // do nothing
        })
      }
      children={
        <Box data-testid="share-project-members">
          {!data?.length || caseUserGroup.length === 0 ? (
            <>
              <Typography variant="body1">No members have access to this project.</Typography>
              <Stack direction="row" spacing={2} sx={{ marginTop: '1rem' }}>
                <Button label="Cancel" size="small" variant="outlined" onClick={handleClose} />
                <Button
                  label="Share"
                  size="small"
                  variant="contained"
                  onClick={async () => handleOpenModal('share')}
                />
              </Stack>
            </>
          ) : (
            caseUserGroup.map(user => (
              <Box
                data-testid={`shared-with-${user.identifier}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  justifyContent: 'space-between',
                  mb: 1,
                }}
                key={user.id}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 29,
                      height: 29,
                      fontSize: 'small',
                      fontWeight: 'bold',
                      margin: '0.2rem',
                    }}
                  >
                    {getInitials(user.identifier)}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {user.identifier}
                  </Typography>
                </Box>
                <PermissionsMenu
                  member={user}
                  onUpdate={handleUpdatePermissions}
                  onRemove={handleRemoveMember}
                />
              </Box>
            ))
          )}
        </Box>
      }
    />
  );
}
export default PermissionsModal;
